import { useEffect, useState } from 'react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  CircularProgress,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material'
import { Close, ContentCopy, ZoomIn } from '@mui/icons-material'
import { ThemedQRCode } from '@/components/ThemedQRCode'
import { useAuthStore } from '@store/authStore'
import { buildShareCodeUrl } from '@features/adventures/utils/shareLink'
import { profileApi } from '@api/endpoints/profile'
import { useQuery } from '@tanstack/react-query'

interface ShareProfileQrDialogProps {
  open: boolean
  onClose: () => void
}

/**
 * Modal opened from the account menu. Renders a QR encoding this person's share-code link,
 * `https://scan.hadv.quest/U<code>` — the same thing the mobile app's share dialog encodes, and
 * the same link the Copy link button hands over.
 *
 * ## Why an ordinary https link rather than a friend token
 *
 * This used to encode `hadv:friend:<userId>`, a custom-scheme payload that only this app's own
 * scanners could read. That made the code useless to the phone already in the other person's hand:
 * their camera app saw an unrecognised string, and the only way to scan it was to install the app
 * first, open it, and find the scanner — which is the wrong order for a code whose entire job is
 * introducing somebody to the app.
 *
 * An https link inverts that. A phone with the app installed opens the app, because it claims every
 * URL on that host; a phone without lands on the scan page, which resolves the code and offers a
 * sign-up. Nothing has to be installed for the code to mean something.
 *
 * The friend-request path still works — better than before, in fact, because it now works from a
 * camera as well as from in-app scanners. The scanners resolve the code and open the profile it
 * points at, where adding somebody is a deliberate button rather than a prompt fired by a scan.
 *
 * Codes made before this change keep working: {@link parseFriendQrPayload} is still wired into all
 * three scanners, so a screenshot somebody saved is not broken by the switch.
 *
 * The thumbnail is click-to-enlarge so the QR stays scannable on dense screens (same pattern as
 * ModeratorApprovalWait).
 */
export default function ShareProfileQrDialog({ open, onClose }: ShareProfileQrDialogProps) {
  const user = useAuthStore((s) => s.user)
  const [enlarged, setEnlarged] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [enlargedSize, setEnlargedSize] = useState(360)

  useEffect(() => {
    if (!enlarged) return
    const compute = () => setEnlargedSize(Math.floor(Math.min(window.innerWidth, window.innerHeight) * 0.8))
    compute()
    window.addEventListener('resize', compute)
    return () => window.removeEventListener('resize', compute)
  }, [enlarged])

  // Reset enlarged state when the outer dialog closes.
  useEffect(() => {
    if (!open) setEnlarged(false)
  }, [open])

  const avatarSrc = user?.avatarUrl || user?.avatar

  // The share code lives on the profile, not on the stored auth user — it is owner-only, so the
  // profile endpoint is the only thing that returns it. Now that the QR encodes the link, this
  // query gates the whole dialog rather than just the copy buttons.
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['profile', 'me'],
    queryFn: () => profileApi.getMyProfile(),
    enabled: open,
  })

  // Shared with its display prefix intact; the server strips it when matching, so it travels as
  // written and somebody reading it aloud says the same thing that was sent.
  const shareCode = profile?.referralCode ? `U${profile.referralCode}` : null
  const shareLink = shareCode ? buildShareCodeUrl(shareCode) : null

  const copy = async (text: string, done: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setToast(done)
    } catch {
      // Clipboard access can be refused outright (insecure context, permissions). The field above
      // is selectable, so say what went wrong rather than silently doing nothing.
      setToast('Could not copy — select the link above instead')
    }
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Share Profile</Typography>
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {!user ? (
            <Alert severity="warning">
              You need to be signed in to share your profile.
            </Alert>
          ) : profileLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : !shareCode || !shareLink ? (
            <Alert severity="info">
              Your personal share code isn&apos;t ready yet. Try again in a moment.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar src={avatarSrc} alt={user?.displayName} sx={{ width: 40, height: 40 }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                    {user?.displayName}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Have a buddy scan this code
                  </Typography>
                </Box>
              </Box>

              <Box
                role="button"
                tabIndex={0}
                onClick={() => setEnlarged(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setEnlarged(true)
                  }
                }}
                sx={{
                  position: 'relative',
                  p: 1.5,
                  bgcolor: 'background.paper',
                  borderRadius: 1.5,
                  border: 1,
                  borderColor: 'divider',
                  cursor: 'pointer',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                  '&:hover': { transform: 'scale(1.03)', boxShadow: 2 },
                  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
                }}
                aria-label="Enlarge profile QR code"
              >
                <ThemedQRCode data={shareLink} size={220} />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 6,
                    right: 6,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(0,0,0,0.55)',
                    color: 'white',
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                  }}
                >
                  <ZoomIn sx={{ fontSize: 16 }} />
                </Box>
              </Box>

              <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                Tap the code to enlarge. Any phone camera can scan it — no app needed.
              </Typography>

              {/* The code itself, spelled out. Somebody across a table can type it, and somebody on
                  a call can read it aloud, neither of which a QR allows. */}
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, letterSpacing: 2, color: 'primary.main', lineHeight: 1 }}
              >
                {shareCode}
              </Typography>

              <Divider flexItem sx={{ width: '100%' }} />

              {/* Same link the QR carries, for the times a camera is not in the room. */}
              <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                Or send the link — it&apos;s the same one the code above points at.
              </Typography>
              <TextField
                value={shareLink}
                size="small"
                fullWidth
                InputProps={{ readOnly: true, sx: { fontSize: '0.8rem' } }}
                onFocus={(e) => e.target.select()}
                aria-label="Your share link"
              />
              <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<ContentCopy />}
                  onClick={() => copy(shareLink, 'Link copied')}
                >
                  Copy link
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ContentCopy />}
                  onClick={() => copy(shareCode, 'Code copied')}
                >
                  Copy code
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <Snackbar
          open={!!toast}
          message={toast ?? ''}
          autoHideDuration={2000}
          onClose={() => setToast(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </Dialog>

      {shareLink && (
        <Dialog
          open={enlarged}
          onClose={() => setEnlarged(false)}
          maxWidth={false}
          PaperProps={{
            sx: {
              bgcolor: 'background.paper',
              p: 3,
              m: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              borderRadius: 2,
            },
          }}
        >
          <IconButton
            onClick={() => setEnlarged(false)}
            size="small"
            aria-label="Close enlarged QR"
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <Close fontSize="small" />
          </IconButton>
          <Box onClick={() => setEnlarged(false)} sx={{ cursor: 'pointer', lineHeight: 0 }}>
            <ThemedQRCode data={shareLink} size={enlargedSize} />
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Tap the code or outside to close
          </Typography>
        </Dialog>
      )}
    </>
  )
}
